import { useCallback, useEffect, useRef, useState } from "react";
import { getPreference, setPreference } from "../../data/preferencesClient.js";

export const SCENARIO_PANEL_KEY = "capacity_scenario_panel";

// The page is keyed by authenticated identity. Reads cannot overwrite a newer
// choice; serialized writes cannot finish out of order. URL replay never saves.
export function useScenarioPanelPreference(userId) {
  const [open, setOpen] = useState(true);
  const [error, setError] = useState(null);
  const revision = useRef(0);
  const queue = useRef(Promise.resolve());
  const alive = useRef(true);
  const latest = useRef(true);
  const failedAction = useRef(null);

  const load = useCallback(() => {
    const initial = revision.current;
    if (!userId) return;
    getPreference(SCENARIO_PANEL_KEY).then((value) => {
      if (!alive.current || revision.current !== initial) return;
      if (value !== null && typeof value !== "boolean") throw new Error("Invalid saved scenario panel choice.");
      latest.current = value ?? true;
      setOpen(latest.current);
      setError(null);
    }).catch((err) => {
      if (alive.current && revision.current === initial) {
        failedAction.current = "load";
        setError(err.message);
      }
    });
  }, [userId]);
  useEffect(() => {
    alive.current = true;
    load();
    return () => { alive.current = false; };
  }, [load]);

  const save = useCallback((value) => {
    const current = ++revision.current;
    latest.current = value;
    setOpen(value);
    setError(null);
    if (!userId) {
      failedAction.current = "save";
      setError("Sign in to save your scenario panel choice.");
      return;
    }
    queue.current = queue.current.then(async () => {
      if (!alive.current) return;
      try {
        await setPreference(SCENARIO_PANEL_KEY, value);
      } catch (err) {
        if (alive.current && revision.current === current) {
          failedAction.current = "save";
          setError(err.message);
        }
      }
    });
  }, [userId]);
  return { open, save, error, retry: () => failedAction.current === "load" ? load() : save(latest.current) };
}
